import React from 'react';

import { classnames } from '@/utils/classnames.ts';

import Slot, { type Slottable } from './Slot.tsx';

export interface CardProps extends React.ComponentPropsWithRef<'section'>, Slottable {
    // Shaded, for a card that's part of what's around it, e.g. in a timeline or among tiles; bordered like any other,
    // since the shaded border alone didn't show.
    isShaded?: boolean;
    // A step darker while hovered or one of its menus is open, and ringed while its link is focused, for a card that's
    // a link or a button or holds actions, as a row is lit (ListRow).
    isHoverable?: boolean;
    // Dashed and without a fill, for a card standing in for one that isn't there, e.g. a client's place without a
    // project among its projects.
    isPlaceholder?: boolean;
}

// Use asChild for a card that's another element, e.g. a button or a sticky header.
const CardRoot = ({
    asChild,
    isShaded = false,
    isHoverable = false,
    isPlaceholder = false,
    className,
    ...props
}: CardProps) => {
    const Comp = asChild ? Slot : 'section';

    return (
        <Comp
            {...props}
            className={classnames(
                'overflow-hidden rounded-card',
                isPlaceholder
                    ? 'border-2 border-dashed border-border'
                    : isShaded
                      ? 'border border-border bg-surface-shaded'
                      : 'border border-border bg-surface-faint',
                isHoverable && [
                    'outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary has-[a[data-focus-visible]]:ring-2 has-[a[data-focus-visible]]:ring-primary',
                    isShaded
                        ? 'hover:bg-surface-muted has-[[aria-expanded=true]]:bg-surface-muted'
                        : 'hover:bg-surface-shaded has-[[aria-expanded=true]]:bg-surface-shaded',
                ],
                className
            )}
        />
    );
};

const CardHeader = ({ className, ...props }: React.ComponentPropsWithRef<'header'>) => (
    <header
        {...props}
        className={classnames(
            'flex min-h-16 items-center justify-between gap-4 border-b border-border px-5',
            className
        )}
    />
);

const CardTitle = ({ className, ...props }: React.ComponentPropsWithRef<'h2'>) => (
    <h2 {...props} className={classnames('text-lg font-medium', className)} />
);

const CardList = ({ className, ...props }: React.ComponentPropsWithRef<'div'>) => (
    <div {...props} className={classnames('divide-y divide-border [&>*]:px-5', className)} />
);

export default Object.assign(CardRoot, {
    Header: CardHeader,
    Title: CardTitle,
    List: CardList,
});
